import type { Achievement } from '../engine/achievements.ts';
import { satisfies } from '../engine/achievements.ts';
import type { Move } from '../engine/modes.ts';

// ***************************************************************************************
// Hunt mode's achievements.
//
// Five standing challenges. Take one and it is struck through and that word scores double.
//
// A claimed row shows WHICH WORD took it, because "you did this at some point" is much
// less satisfying than "MYSTIFY did this", and because a player who claimed two with one
// word deserves to see that.
//
// The live highlight uses the engine's own `satisfies`, not a copy of the rule. A panel
// that lit up for a word the engine would not credit is worse than no highlight at all.
// ***************************************************************************************

export function HuntPanel({
  achievements,
  moves,
  live,
}: {
  achievements: readonly Achievement[];
  moves: readonly Move[];
  /** The selection being made, priced, when it is a word the game would accept. */
  live: Move | null;
}): React.ReactElement | null {
  if (achievements.length === 0) return null;

  // Which word took which. First claim wins - an id cannot be claimed twice.
  const takenBy = new Map<string, Move>();

  for (const move of moves) {
    for (const id of move.claimed) {
      if (!takenBy.has(id)) takenBy.set(id, move);
    }
  }

  const done = takenBy.size;

  return (
    <section className="hunt" aria-label="Achievements">
      <div className="hunt__head">
        <h2 className="hunt__title">Achievements</h2>
        <span className="hunt__count">
          {done} of {achievements.length}
        </span>
      </div>

      <ul className="hunt__list">
        {achievements.map((achievement) => {
          const claimedBy = takenBy.get(achievement.id);
          const wouldTake = !claimedBy && live !== null && satisfies(achievement, live);

          return (
            <li
              key={achievement.id}
              className={
                'hunt__item' +
                (claimedBy ? ' hunt__item--done' : '') +
                (wouldTake ? ' hunt__item--live' : '')
              }
            >
              <span className="hunt__mark" aria-hidden="true">
                {claimedBy ? '✓' : wouldTake ? '★' : '○'}
              </span>

              <span className="hunt__body">
                <span className="hunt__label">{achievement.label}</span>

                {claimedBy ? (
                  <span className="hunt__by">
                    {claimedBy.word.toUpperCase()} · {claimedBy.score}
                  </span>
                ) : wouldTake ? (
                  <span className="hunt__by hunt__by--live">
                    this word takes it — double points
                  </span>
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>

      <p className="hunt__note">
        Take one and that word scores double. Take two with the same word and it scores
        triple.
      </p>
    </section>
  );
}
